import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { formatFileSize } from '../../lib/utils';
import {
  Trash2,
  Database,
  HardDrive,
  Users,
  FileText,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Shield,
  RefreshCw,
  UserMinus,
  FileWarning,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const AdminMaintenance = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  // Bulk delete form
  const [bulkDeleteDate, setBulkDeleteDate] = useState('');
  const [bulkDeleteStatus, setBulkDeleteStatus] = useState('all');

  // Deactivate form
  const [inactiveMonths, setInactiveMonths] = useState('12');

  // Cleanup files
  const [cleanupDays, setCleanupDays] = useState('365');

  // Retention settings
  const [retention, setRetention] = useState({
    auto_purge_enabled: false,
    retention_days: 730,
    auto_deactivate_enabled: false,
    inactive_months: 12
  });

  const fetchStats = async () => {
    try {
      const res = await api.get('/maintenance/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load system stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchRetention = async () => {
    try {
      const res = await api.get('/maintenance/retention-settings');
      setRetention(res.data);
    } catch (err) {
      console.error('Failed to load retention settings');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRetention();
  }, []);

  const handleAction = async (action, config = {}) => {
    const confirmMsg = config.confirmMsg || 'Are you sure? This action cannot be undone.';
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(action);
    try {
      let res;
      switch (action) {
        case 'purge-errors':
          res = await api.post('/maintenance/purge-errors');
          break;
        case 'bulk-delete':
          if (!bulkDeleteDate) {
            toast.error('Please select a date');
            setActionLoading('');
            return;
          }
          res = await api.post('/maintenance/bulk-delete', {
            before_date: new Date(bulkDeleteDate).toISOString(),
            status_filter: bulkDeleteStatus
          });
          break;
        case 'cleanup-orphans':
          res = await api.post('/maintenance/cleanup-orphans');
          break;
        case 'deactivate-inactive':
          res = await api.post(`/maintenance/deactivate-inactive?months=${inactiveMonths}`);
          break;
        case 'cleanup-files':
          res = await api.post(`/maintenance/cleanup-files?days=${cleanupDays}`);
          break;
        default:
          return;
      }
      toast.success(res.data.message);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleExport = async (dataType) => {
    setActionLoading(`export-${dataType}`);
    try {
      const res = await api.get(`/maintenance/export?data_type=${dataType}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rba_backup_${dataType}_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${dataType} data exported successfully`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleRetentionSave = async () => {
    setActionLoading('retention');
    try {
      await api.patch('/maintenance/retention-settings', retention);
      toast.success('Retention settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-maintenance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-foreground">System Maintenance</h1>
            <p className="text-muted-foreground mt-1">Manage storage, data cleanup, and system health</p>
          </div>
          <Button variant="outline" onClick={() => { setLoading(true); fetchStats(); }} data-testid="refresh-stats-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* System Health Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="font-mono text-3xl font-bold text-foreground">{stats.users.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stats.users.active} active / {stats.users.inactive} inactive</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Submissions</p>
                    <p className="font-mono text-3xl font-bold text-foreground">{stats.submissions.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stats.submissions.errors > 0 && <span className="text-red-500">{stats.submissions.errors} errors</span>}
                      {stats.submissions.errors > 0 && ' / '}
                      {stats.submissions.completed} completed
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                    <FileText className="w-6 h-6 text-emerald-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">DB Storage</p>
                    <p className="font-mono text-3xl font-bold text-foreground">{stats.storage.db_total_mb}</p>
                    <p className="text-xs text-muted-foreground mt-1">MB in database</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center">
                    <Database className="w-6 h-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Disk Usage</p>
                    <p className="font-mono text-3xl font-bold text-foreground">{stats.storage.disk_mb}</p>
                    <p className="text-xs text-muted-foreground mt-1">MB uploaded files</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-sm flex items-center justify-center">
                    <HardDrive className="w-6 h-6 text-amber-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purge Error Submissions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-red-500" />
                Purge Failed Submissions
              </CardTitle>
              <CardDescription>
                Remove all submissions with error status and their uploaded files
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.submissions.errors > 0 ? (
                <div className="space-y-3">
                  <Badge variant="destructive" className="text-sm">
                    {stats.submissions.errors} error submissions found
                  </Badge>
                  <Button
                    variant="destructive"
                    onClick={() => handleAction('purge-errors', {
                      confirmMsg: `Delete ${stats.submissions.errors} error submissions and their files? This cannot be undone.`
                    })}
                    disabled={actionLoading === 'purge-errors'}
                    data-testid="purge-errors-btn"
                  >
                    {actionLoading === 'purge-errors' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Purge Error Submissions
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">No error submissions found</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cleanup Orphaned Results */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-amber-500" />
                Cleanup Orphaned Data
              </CardTitle>
              <CardDescription>
                Remove scan results that no longer belong to any submission
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.scan_results.orphaned > 0 ? (
                <div className="space-y-3">
                  <Badge variant="secondary" className="text-sm bg-amber-100 text-amber-700">
                    {stats.scan_results.orphaned} orphaned records found
                  </Badge>
                  <Button
                    variant="outline"
                    onClick={() => handleAction('cleanup-orphans', {
                      confirmMsg: `Remove ${stats.scan_results.orphaned} orphaned scan results?`
                    })}
                    disabled={actionLoading === 'cleanup-orphans'}
                    data-testid="cleanup-orphans-btn"
                  >
                    {actionLoading === 'cleanup-orphans' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Cleanup Orphans
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">No orphaned data found</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bulk Delete by Date */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Bulk Delete Submissions
              </CardTitle>
              <CardDescription>Delete submissions older than a specific date</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Delete submissions before</Label>
                <Input
                  type="date"
                  value={bulkDeleteDate}
                  onChange={(e) => setBulkDeleteDate(e.target.value)}
                  className="h-10"
                  data-testid="bulk-delete-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Status filter</Label>
                <Select value={bulkDeleteStatus} onValueChange={setBulkDeleteStatus}>
                  <SelectTrigger className="h-10" data-testid="bulk-delete-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="completed">Completed only</SelectItem>
                    <SelectItem value="error">Errors only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="destructive"
                onClick={() => handleAction('bulk-delete', {
                  confirmMsg: `Delete all ${bulkDeleteStatus} submissions before ${bulkDeleteDate}? This cannot be undone.`
                })}
                disabled={!bulkDeleteDate || actionLoading === 'bulk-delete'}
                data-testid="bulk-delete-btn"
              >
                {actionLoading === 'bulk-delete' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete Submissions
              </Button>
            </CardContent>
          </Card>

          {/* Deactivate Inactive Users */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserMinus className="w-5 h-5 text-amber-500" />
                Deactivate Inactive Users
              </CardTitle>
              <CardDescription>Deactivate student accounts with no submissions in X months</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Inactive period (months)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={inactiveMonths}
                  onChange={(e) => setInactiveMonths(e.target.value)}
                  className="h-10"
                  data-testid="inactive-months-input"
                />
              </div>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => handleAction('deactivate-inactive', {
                  confirmMsg: `Deactivate all students with no submissions in ${inactiveMonths} months?`
                })}
                disabled={actionLoading === 'deactivate-inactive'}
                data-testid="deactivate-inactive-btn"
              >
                {actionLoading === 'deactivate-inactive' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserMinus className="w-4 h-4 mr-2" />}
                Deactivate Inactive
              </Button>
            </CardContent>
          </Card>

          {/* Cleanup Old Files */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-purple-500" />
                Cleanup Old Files
              </CardTitle>
              <CardDescription>Remove uploaded files older than X days (DB records are kept)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Files older than (days)</Label>
                <Input
                  type="number"
                  min="30"
                  max="3650"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(e.target.value)}
                  className="h-10"
                  data-testid="cleanup-days-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => handleAction('cleanup-files', {
                  confirmMsg: `Remove uploaded files older than ${cleanupDays} days? DB records will be kept.`
                })}
                disabled={actionLoading === 'cleanup-files'}
                data-testid="cleanup-files-btn"
              >
                {actionLoading === 'cleanup-files' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HardDrive className="w-4 h-4 mr-2" />}
                Cleanup Files
              </Button>
            </CardContent>
          </Card>

          {/* Data Export / Backup */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-500" />
                Data Export / Backup
              </CardTitle>
              <CardDescription>Download system data as JSON backup files</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleExport('all')}
                disabled={actionLoading === 'export-all'}
                data-testid="export-all-btn"
              >
                {actionLoading === 'export-all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export All Data
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleExport('users')}
                disabled={actionLoading === 'export-users'}
                data-testid="export-users-btn"
              >
                {actionLoading === 'export-users' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Export Users Only
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleExport('submissions')}
                disabled={actionLoading === 'export-submissions'}
                data-testid="export-submissions-btn"
              >
                {actionLoading === 'export-submissions' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Export Submissions Only
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Retention Policy Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-foreground" />
              Automated Retention Policy
            </CardTitle>
            <CardDescription>Configure automatic data lifecycle management rules</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4 p-4 rounded-sm border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">Auto-Purge Old Submissions</p>
                    <p className="text-sm text-muted-foreground">Automatically delete submissions after retention period</p>
                  </div>
                  <Switch
                    checked={retention.auto_purge_enabled}
                    onCheckedChange={(checked) => setRetention(prev => ({ ...prev, auto_purge_enabled: checked }))}
                    data-testid="auto-purge-toggle"
                  />
                </div>
                {retention.auto_purge_enabled && (
                  <div className="space-y-2">
                    <Label>Retention period (days)</Label>
                    <Input
                      type="number"
                      min="30"
                      max="3650"
                      value={retention.retention_days}
                      onChange={(e) => setRetention(prev => ({ ...prev, retention_days: parseInt(e.target.value) || 730 }))}
                      className="h-10"
                      data-testid="retention-days-input"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4 p-4 rounded-sm border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">Auto-Deactivate Inactive Users</p>
                    <p className="text-sm text-muted-foreground">Deactivate accounts with no activity</p>
                  </div>
                  <Switch
                    checked={retention.auto_deactivate_enabled}
                    onCheckedChange={(checked) => setRetention(prev => ({ ...prev, auto_deactivate_enabled: checked }))}
                    data-testid="auto-deactivate-toggle"
                  />
                </div>
                {retention.auto_deactivate_enabled && (
                  <div className="space-y-2">
                    <Label>Inactive period (months)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={retention.inactive_months}
                      onChange={(e) => setRetention(prev => ({ ...prev, inactive_months: parseInt(e.target.value) || 12 }))}
                      className="h-10"
                      data-testid="auto-inactive-months-input"
                    />
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleRetentionSave}
              disabled={actionLoading === 'retention'}
              data-testid="save-retention-btn"
            >
              {actionLoading === 'retention' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Retention Settings
            </Button>
          </CardContent>
        </Card>

        {/* Warning */}
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            All deletion actions are permanent and cannot be reversed. Always export a backup before performing bulk operations.
          </AlertDescription>
        </Alert>
      </div>
    </Layout>
  );
};

export default AdminMaintenance;
